import React, { useEffect, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import Icon from "../../components/icon";
import { useLocation, useNavigate } from "react-router-dom";
import ServerSideNavItems from "../../configs/app-navigation2";

// 🔄 fungsi untuk flatten nested menu ke array datar
function flattenMenu(items: any[], category?: string): any[] {
  let result: any[] = []
  items.forEach(item => {
    if (typeof item.path === 'string' && item.path.trim() && item.path !== '#') {
      result.push({
        category: category || item.text,
        label: item.text,
        path: item.path.startsWith('/') ? item.path : `/${item.path}`,
        icon: item.icon,
      })
    }
    if (item.items && item.items.length > 0) {
      result = result.concat(flattenMenu(item.items, [category, item.text].filter(Boolean).join(' / ')))
    }
  })
  return result
}

export default function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { menuItems, loading, error } = ServerSideNavItems(open);
  const navigate = useNavigate();
  const location = useLocation();

  const selectMenu = (path: string) => {
    // Close the portal before rendering the destination, including the current route.
    flushSync(() => {
      setOpen(false);
      setQuery('');
    });
    navigate(path);
  };

  useEffect(() => {
    setOpen(false);
    setQuery('');
  }, [location.key]);

  const commands = flattenMenu(menuItems || []);

  // buka/tutup pakai keyboard shortcut Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/" && !e.repeat) {
        e.preventDefault();
        setQuery('');
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // filter berdasarkan query
  const filtered = commands.filter((cmd) =>
    `${cmd.category} ${cmd.label} ${cmd.path}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        .gcp-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 10vh;
          z-index: 2000;
        }
        .gcp-container {
          width: min(600px, 94vw);
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
          font-family: system-ui, sans-serif;
          animation: fadeIn 0.15s ease-out;
        }
        .gcp-header {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid #eee;
        }
        .gcp-header input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          padding: 8px;
          border-radius: 6px;
          background: #f7f7f7;
        }
        .gcp-header .esc {
          margin-left: 12px;
          font-size: 12px;
          color: #777;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 2px 6px;
        }
        .gcp-list {
          max-height: 320px;
          overflow-y: auto;
        }
        .gcp-item {
          display: flex;
          align-items: center;
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .gcp-item:hover {
          background: #f5f5f5;
        }
        .gcp-icon {
          font-size: 18px;
          margin-right: 12px;
        }
        .gcp-label {
          flex: 1;
          font-size: 14px;
        }
        .gcp-label .gcp-category {
          font-size: 11px;
          font-weight: 600;
          color: #999;
          margin-right: 8px;
          text-transform: uppercase;
        }
        .gcp-empty {
          padding: 20px;
          text-align: center;
          color: #888;
          font-size: 14px;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="gcp-overlay" onClick={() => setOpen(false)}>
        <div className="gcp-container" role="dialog" aria-modal="true" aria-label="Cari menu" onClick={e => e.stopPropagation()}>
          <div className="gcp-header">
            <input
              type="text"
              placeholder="Cari menu yang bisa diakses..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <span className="esc">Esc</span>
          </div>

          <div className="gcp-list">
            {(loading || error || filtered.length === 0) && (
              <div className="gcp-empty">{loading ? 'Memuat menu...' : error || 'Menu tidak ditemukan'}</div>
            )}

            {filtered.map((cmd, idx) => (
              <div
                key={idx}
                className="gcp-item"
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectMenu(cmd.path);
                  }
                }}
                onClick={() => selectMenu(cmd.path)}
              >
                <span className="gcp-icon">
                  <Icon icon={cmd.icon ? (cmd.icon.includes(':') ? cmd.icon : `mdi:${cmd.icon}`) : "mdi:circle"} />
                </span>
                <span className="gcp-label">
                  <span className="gcp-category">{cmd.category}</span>
                  {cmd.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
